import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Phone, Mail, MapPin, Plus, Send, Loader2,
  FileText, Calendar, HardHat, CalendarPlus, Archive, ArchiveRestore, Trash2, Mic, Building2,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, eur, dateFr, dateTimeFr, apiError, API, downloadFile } from "@/lib/api";
import StatutBadge from "@/components/StatutBadge";
import Modal from "@/components/Modal";
import PhotoCapture from "@/components/PhotoCapture";

export default function ClientDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [compteRendus, setCompteRendus] = useState([]);
  const [chantierOpen, setChantierOpen] = useState(false);
  const [chantierForm, setChantierForm] = useState({ titre: "", adresse: "" });
  const [msg, setMsg] = useState("");
  const [canal, setCanal] = useState("email");
  const [busy, setBusy] = useState(false);
  const [supprOpen, setSupprOpen] = useState(false);

  const CANAL_ICON = { email: t("client_detail.canal_email"), note: t("client_detail.canal_note"), sms: t("client_detail.canal_sms"), whatsapp: t("client_detail.canal_whatsapp") };

  const load = () => {
    api.get(`/clients/${id}`).then((r) => setData(r.data)).catch(() => navigate("/clients"));
    api.get(`/clients/${id}/messages`).then((r) => setMessages(r.data));
    api.get(`/photos?client_id=${id}`).then((r) => setPhotos(r.data));
    api.get(`/comptes-rendus?client_id=${id}`).then((r) => setCompteRendus(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="py-20 text-center text-stone-500">{t("commun.chargement")}</div>;
  const { client, chantiers, devis, factures, rendezvous, stats } = data;
  const isArchive = client.statut === "archive";

  async function archiver() {
    try {
      await api.post(`/clients/${id}/archiver`);
      toast.success(t("client_detail.archive_toast"));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  async function desarchiver() {
    try {
      await api.post(`/clients/${id}/desarchiver`);
      toast.success(t("client_detail.restaure_toast"));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  async function supprimer() {
    setBusy(true);
    try {
      await api.delete(`/clients/${id}`);
      toast.success(t("client_detail.supprime_toast"));
      navigate("/clients");
    } catch (e) {
      toast.error(apiError(e));
      setSupprOpen(false);
      setBusy(false);
    }
  }

  const timeline = [
    ...devis.map((d) => ({ kind: "devis", date: d.cree_le, label: `${t("client_detail.devis_label")} ${d.numero}`, montant: d.total_ttc, statut: d.statut, link: `/devis/${d.id}` })),
    ...factures.map((f) => ({ kind: "facture", date: f.cree_le, label: `${t("client_detail.facture_label")} ${f.numero}`, montant: f.montant_ttc, statut: f.statut, link: `/factures/${f.id}` })),
    ...rendezvous.map((r) => ({ kind: "rdv", id: r.id, date: r.date_debut, label: `${t("client_detail.rdv_label")} : ${r.titre}`, statut: null, link: "/agenda" })),
    ...compteRendus.map((c) => ({ kind: "cr", id: c.id, date: c.cree_le, label: `${t("client_detail.cr_label")} : ${c.titre}`, statut: null, link: `/comptes-rendus?ouvrir=${c.id}` })),
  ].sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  async function createChantier() {
    if (!chantierForm.titre.trim()) return toast.error(t("client_detail.chantier_titre_requis"));
    setBusy(true);
    try {
      await api.post("/chantiers", { client_id: id, ...chantierForm });
      toast.success(t("client_detail.chantier_cree"));
      setChantierOpen(false);
      setChantierForm({ titre: "", adresse: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function envoyerMessage() {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await api.post(`/clients/${id}/messages`, { canal, contenu: msg });
      setMsg("");
      toast.success(t("client_detail.message_envoye"));
      api.get(`/clients/${id}/messages`).then((r) => setMessages(r.data));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function onPhotoUploaded(photo) {
    setPhotos([photo, ...photos]);
    const chantier = chantiers.find((c) => c.id === photo.chantier_id);
    await api.post(`/clients/${id}/messages`, {
      canal: "note",
      contenu: t("client_detail.photo_note", { chantier: chantier ? ` « ${chantier.titre} »` : "" }),
      chantier_id: photo.chantier_id,
    }).catch(() => {});
    api.get(`/clients/${id}/messages`).then((r) => setMessages(r.data));
  }

  return (
    <div className="space-y-5" data-testid="page-client-detail">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/clients")} className="w-11 h-11 flex items-center justify-center rounded-lg bg-white border border-[#E6DFD1]"
                data-testid="btn-retour-clients">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-serif font-bold text-2xl sm:text-3xl truncate">
            {client.nom}
            {isArchive && (
              <span className="inline-flex items-center gap-1 align-middle ml-2 text-xs font-sans font-semibold px-2 py-0.5 rounded bg-[#EFEAE0] text-[#8A5A2B]"
                    data-testid="badge-client-archive">
                <Archive size={11} /> {t("client_detail.archive")}
              </span>
            )}
            {client.type_client === "professionnel" && (
              <span className="inline-flex items-center gap-1 align-middle ml-2 text-xs font-sans font-semibold px-2 py-0.5 rounded bg-[#DCE8F5] text-[#1D4E7E]"
                    data-testid="badge-client-pro">
                <Building2 size={11} /> {t("client_detail.pro")}
              </span>
            )}
          </h1>
          <div className="text-stone-500 text-sm flex flex-wrap gap-x-4 gap-y-1 mt-0.5">
            {client.telephone && <a href={`tel:${client.telephone}`} className="flex items-center gap-1"><Phone size={13} /> {client.telephone}</a>}
            {client.email && <span className="flex items-center gap-1"><Mail size={13} /> {client.email}</span>}
            {client.adresse && <span className="flex items-center gap-1"><MapPin size={13} /> {client.adresse}</span>}
          </div>
          {(client.raison_sociale || client.siret || client.tva_intracom) && (
            <div className="text-stone-500 text-xs flex flex-wrap gap-x-4 gap-y-1 mt-1" data-testid="infos-pro-client">
              {client.raison_sociale && client.raison_sociale !== client.nom && <span>{client.raison_sociale}</span>}
              {client.siret && <span>{t("client_detail.siret_label")} : {client.siret}</span>}
              {client.tva_intracom && <span>{t("client_detail.tva_label")} : {client.tva_intracom}</span>}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="panel">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-serif font-semibold text-lg flex items-center gap-2"><HardHat size={18} /> {t("client_detail.chantiers")}</h2>
              {!isArchive && (
                <button onClick={() => setChantierOpen(true)} className="btn-secondary !h-10 !px-3 !text-sm"
                        data-testid="btn-nouveau-chantier">
                  <Plus size={16} /> {t("client_detail.chantier")}
                </button>
              )}
            </div>
            {chantiers.length === 0 ? (
              <p className="text-stone-500 text-sm">{t("client_detail.vide_chantiers")}</p>
            ) : (
              <div className="space-y-2" data-testid="liste-chantiers">
                {chantiers.map((c) => (
                  <div key={c.id} className="flex items-center justify-between border border-[#E6DFD1] rounded-lg p-3">
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{c.titre}</div>
                      <div className="text-xs text-stone-500 truncate">{c.adresse}</div>
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border shrink-0 ${
                      c.statut === "en_cours" ? "bg-[#FCEFD9] text-[#8A5A0F] border-[#F6D9A9]" : "bg-[#E4F2E7] text-[#276440] border-[#B8E2C2]"}`}>
                      {c.statut === "en_cours" ? t("client_detail.en_cours") : t("client_detail.termine")}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="panel">
            <h2 className="font-serif font-semibold text-lg mb-3">{t("client_detail.historique")}</h2>
            {timeline.length === 0 ? (
              <p className="text-stone-500 text-sm">{t("client_detail.vide_historique")}</p>
            ) : (
              <div className="divide-y divide-[#E6DFD1]" data-testid="timeline-client">
                {timeline.map((l, i) => (
                  <Link key={i} to={l.link} className="flex items-center gap-3 py-3 hover:bg-[#FDFBF7] px-1 rounded">
                    {l.kind === "rdv" ? <Calendar size={16} className="text-[#8A5A2B] shrink-0" />
                      : l.kind === "cr" ? <Mic size={16} className="text-[#8A5A2B] shrink-0" />
                      : <FileText size={16} className="text-[#8A5A2B] shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{l.label}</div>
                      <div className="text-xs text-stone-500">{l.kind === "rdv" ? dateTimeFr(l.date) : dateFr(l.date)}</div>
                    </div>
                    {l.montant != null && <span className="font-serif font-semibold text-sm shrink-0">{eur(l.montant)}</span>}
                    {l.statut && <StatutBadge statut={l.statut} />}
                    {l.kind === "rdv" && l.id && (
                      <span role="button" title={t("agenda.ics_title")}
                            data-testid={`btn-ics-rdv-${l.id}`}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); downloadFile(`/rendezvous/${l.id}/ics`, `rdv-${l.id}.ics`); }}
                            className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-[#E6DFD1] text-[#8A5A2B] shrink-0">
                        <CalendarPlus size={15} />
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {photos.length > 0 && (
            <div className="panel">
              <h2 className="font-serif font-semibold text-lg mb-3">{t("client_detail.photos")}</h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="galerie-photos">
                {photos.map((p) => (
                  <a key={p.id} href={`${API}/photos/${p.id}/file`} target="_blank" rel="noreferrer">
                    <img src={`${API}/photos/${p.id}/file`} alt={p.description || t("photo.alt_chantier")}
                         className="w-full h-24 object-cover rounded-lg border border-[#E6DFD1]" loading="lazy" />
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="panel" data-testid="fil-conversation">
            <h2 className="font-serif font-semibold text-lg mb-3">{t("client_detail.echanges")}</h2>
            <div className="space-y-2 mb-4 max-h-80 overflow-y-auto" data-testid="liste-messages">
              {messages.length === 0 && (
                <p className="text-stone-500 text-sm">{t("client_detail.vide_messages")}</p>
              )}
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.sens === "envoye" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm ${
                    m.sens === "envoye" ? "bg-[#12100D] text-stone-100" : "bg-white border border-[#E6DFD1]"}`}>
                    <div className="whitespace-pre-wrap">{m.contenu}</div>
                    <div className={`text-[10px] mt-1 ${m.sens === "envoye" ? "text-stone-400" : "text-stone-500"}`}>
                      {CANAL_ICON[m.canal] || m.canal} · {dateTimeFr(m.date)}
                      {m.type !== "manuel" && <> · {t("commun.automatique")}</>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {!isArchive && (
              <div className="flex gap-2">
                <input className="input-field" placeholder={t("client_detail.message_placeholder")} value={msg}
                       onChange={(e) => setMsg(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && envoyerMessage()}
                       data-testid="input-message-client" />
                <PhotoCapture clientId={id} chantierId={chantiers[0]?.id || ""} onUploaded={onPhotoUploaded}
                              className="btn-secondary !px-3.5 shrink-0" testid="btn-photo-message">
                  📷
                </PhotoCapture>
                <button onClick={envoyerMessage} disabled={busy || !msg.trim()} className="btn-accent !px-4 shrink-0"
                        data-testid="btn-envoyer-message">
                  {busy ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-[#12100D] rounded-xl p-5 border border-[#D9A461]/30" data-testid="panel-totaux-client">
            <div className="text-stone-400 text-sm">{t("client_detail.ca_total")}</div>
            <div className="amount-display mt-1">{eur(stats.total_facture)}</div>
            <div className="border-t border-white/10 mt-4 pt-4">
              <div className="text-stone-400 text-sm">{t("client_detail.en_attente")}</div>
              <div className={`font-serif font-bold text-xl mt-1 ${stats.en_attente > 0 ? "text-[#D9A461]" : "text-[#7BC496]"}`}>
                {eur(stats.en_attente)}
              </div>
            </div>
          </div>
          {isArchive ? (
            <>
              <p className="text-xs text-stone-500 text-center" data-testid="note-client-archive">
                {t("client_detail.note_archive")}
              </p>
              <button onClick={desarchiver} className="btn-secondary w-full" data-testid="btn-desarchiver-client">
                <ArchiveRestore size={18} /> {t("client_detail.restaurer")}
              </button>
            </>
          ) : (
            <>
              <Link to={`/devis/nouveau`} className="btn-accent w-full" data-testid="btn-devis-pour-client">
                <Plus size={18} /> {t("dash.nouveau_devis")}
              </Link>
              <Link to="/agenda" className="btn-secondary w-full" data-testid="btn-rdv-pour-client">
                <Calendar size={18} /> {t("client_detail.planifier_rdv")}
              </Link>
              <button onClick={archiver} className="btn-secondary w-full" data-testid="btn-archiver-client">
                <Archive size={18} /> {t("client_detail.archiver")}
              </button>
              {devis.length === 0 && factures.length === 0 && (
                <button onClick={() => setSupprOpen(true)} className="btn-secondary w-full !text-[#A32E20]"
                        data-testid="btn-supprimer-client">
                  <Trash2 size={18} /> {t("client_detail.supprimer")}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <Modal open={supprOpen} onClose={() => setSupprOpen(false)} title={t("client_detail.supprimer_titre")} testid="modal-supprimer-client">
        <p className="text-sm text-stone-600 mb-5">{t("client_detail.supprimer_texte", { nom: client.nom })}</p>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => setSupprOpen(false)} className="btn-secondary" data-testid="btn-annuler-suppression">
            {t("commun.annuler")}
          </button>
          <button onClick={supprimer} disabled={busy} className="btn-accent !bg-[#A32E20]" data-testid="btn-confirmer-suppression">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <Trash2 size={18} />} {t("commun.supprimer")}
          </button>
        </div>
      </Modal>

      <Modal open={chantierOpen} onClose={() => setChantierOpen(false)} title={t("client_detail.chantier_titre")} testid="modal-nouveau-chantier">
        <div className="space-y-4">
          <input className="input-field" placeholder={t("client_detail.chantier_placeholder")} value={chantierForm.titre}
                 onChange={(e) => setChantierForm({ ...chantierForm, titre: e.target.value })}
                 data-testid="input-chantier-titre" />
          <input className="input-field" placeholder={t("client_detail.chantier_adresse")} value={chantierForm.adresse}
                 onChange={(e) => setChantierForm({ ...chantierForm, adresse: e.target.value })}
                 data-testid="input-chantier-adresse" />
          <button onClick={createChantier} disabled={busy} className="btn-accent w-full" data-testid="btn-confirmer-chantier">
            {busy ? <Loader2 className="animate-spin" size={18} /> : t("client_detail.chantier_creer")}
          </button>
        </div>
      </Modal>
    </div>
  );
}
